import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { catchError, concatMap, filter, map, switchMap, tap } from 'rxjs/operators'

import { IntegrationService } from './service'
import { ActionFailed, InfoDialogService } from '@tokeet-frontend/tv3-platform'
import { of } from 'rxjs'
import {
  ConnectChekin,
  ConnectChekinComplete,
  ConnectHolded,
  ConnectHoldedComplete,
  ConnectWheelhouse,
  ConnectWheelhouseComplete,
  DisconnectChekin,
  DisconnectChekinComplete,
  DisconnectHolded,
  DisconnectHoldedComplete,
  DisconnectQuickBooks,
  DisconnectQuickBooksComplete,
  DisconnectWheelhouse,
  DisconnectWheelhouseComplete,
  LoadAuthorizedIntegration,
  LoadAuthorizedIntegrationComplete,
  LoadAuthorizeIntegrationConfig,
  LoadAuthorizeIntegrationConfigComplete,
  UnauthorizeIntegration,
  UnauthorizeIntegrationComplete,
} from './actions'
import { Toaster } from '@tokeet-frontend/tv3-platform'
import { ThirdPartyIntegrationIdentifies } from './model'

@Injectable()
export class IntegrationEffects {
  // maybe multiple are required
  @Effect()
  loadAuthData$ = this.actions$.pipe(
    ofType(LoadAuthorizedIntegration),
    concatMap(({ integration }) => {
      if (integration === ThirdPartyIntegrationIdentifies.Wheelhouse) {
        return this.integration.getWheelhouseStatus().pipe(
          filter((status) => status),
          map((connected) => LoadAuthorizedIntegrationComplete({ data: { connected: true }, integration }))
        )
      }
      if (integration === ThirdPartyIntegrationIdentifies.Chekin) {
        return this.integration.getChekinStatus().pipe(
          filter((status) => status),
          map((connected) => LoadAuthorizedIntegrationComplete({ data: { connected: true }, integration }))
        )
      }
      if (integration === ThirdPartyIntegrationIdentifies.Holded) {
        return this.integration
          .getHoldedStatus()
          .pipe(
            map((connected) =>
              LoadAuthorizedIntegrationComplete({ data: connected ? { connected } : null, integration })
            )
          )
      }
      if (integration === ThirdPartyIntegrationIdentifies.Quickbooks) {
        return this.integration.getQuickBooksStatus().pipe(
          map((status) => !!status.connected),
          map((connected) => LoadAuthorizedIntegrationComplete({ data: connected ? { connected } : null, integration }))
        )
      }
      return this.integration.getOAuth(integration).pipe(
        map((data) => LoadAuthorizedIntegrationComplete({ data, integration })),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  getConfig$ = this.actions$.pipe(
    ofType(LoadAuthorizeIntegrationConfig),
    switchMap(({ integration }) =>
      this.integration.getConfig(integration).pipe(
        map((data) => LoadAuthorizeIntegrationConfigComplete({ data, integration })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  unauthorize$ = this.actions$.pipe(
    ofType(UnauthorizeIntegration),
    switchMap(({ integration }) =>
      this.integration.unauthorize(integration).pipe(
        map(() => UnauthorizeIntegrationComplete({ integration })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  connectWheelhouse$ = this.actions$.pipe(
    ofType(ConnectWheelhouse),
    switchMap(({ apiKey }) =>
      this.integration.connectWheelhouse(apiKey).pipe(
        tap(() => this.toaster.success($localize`Wheelhouse connected successfully.`)),
        map(() => ConnectWheelhouseComplete({ integration: ThirdPartyIntegrationIdentifies.Wheelhouse })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  disconnectWheelhouse$ = this.actions$.pipe(
    ofType(DisconnectWheelhouse),
    switchMap(() =>
      this.integration.disconnectWheelhouse().pipe(
        tap(() => this.toaster.success($localize`Wheelhouse disconnected successfully.`)),
        switchMap(() => [
          DisconnectWheelhouseComplete({ integration: ThirdPartyIntegrationIdentifies.Wheelhouse }),
          UnauthorizeIntegrationComplete({ integration: ThirdPartyIntegrationIdentifies.Wheelhouse }),
        ]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  connectChekin$ = this.actions$.pipe(
    ofType(ConnectChekin),
    switchMap(({ accountType }) =>
      this.integration.connectChekin(accountType).pipe(
        switchMap((data) => {
          if (data.success && data.email && data.password) {
            return this.info.open({
              title: $localize`Chekin connected successfully.`,
              body: `Below are the credentials for Chekin. <br/><br/> <b>Email</b>: ${data.email} <br/> <b>Password</b>: ${data.password} <br/><br/> Please save these credentials because these credentials will not be accessible again inside AdvanceCM.`,
            })
          } else {
            return of(data)
          }
        }),
        map(() => ConnectChekinComplete({ integration: ThirdPartyIntegrationIdentifies.Chekin })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  disconnectChekin$ = this.actions$.pipe(
    ofType(DisconnectChekin),
    switchMap(() =>
      this.integration.disconnectChekin().pipe(
        tap(() => this.toaster.success($localize`Chekin disconnected successfully.`)),
        switchMap(() => [
          DisconnectChekinComplete({ integration: ThirdPartyIntegrationIdentifies.Chekin }),
          UnauthorizeIntegrationComplete({ integration: ThirdPartyIntegrationIdentifies.Chekin }),
        ]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  connectHolded$ = this.actions$.pipe(
    ofType(ConnectHolded),
    switchMap(({ apiKey }) =>
      this.integration.connectHolded(apiKey).pipe(
        tap(() => this.toaster.success($localize`Holded connected successfully.`)),
        map(() => ConnectHoldedComplete({ integration: ThirdPartyIntegrationIdentifies.Holded })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  disconnectHolded$ = this.actions$.pipe(
    ofType(DisconnectHolded),
    switchMap(() =>
      this.integration.disconnectHolded().pipe(
        tap(() => this.toaster.success($localize`Holded disconnected successfully.`)),
        switchMap(() => [
          DisconnectHoldedComplete({ integration: ThirdPartyIntegrationIdentifies.Holded }),
          UnauthorizeIntegrationComplete({ integration: ThirdPartyIntegrationIdentifies.Holded }),
        ]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  disconnectQuickBooks$ = this.actions$.pipe(
    ofType(DisconnectQuickBooks),
    switchMap(() =>
      this.integration.disconnectQuickBooks().pipe(
        tap(() => this.toaster.success($localize`QuickBooks disconnected successfully.`)),
        switchMap(() => [
          DisconnectQuickBooksComplete({ integration: ThirdPartyIntegrationIdentifies.Quickbooks }),
          UnauthorizeIntegrationComplete({ integration: ThirdPartyIntegrationIdentifies.Quickbooks }),
        ]),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(
    private actions$: Actions,
    private integration: IntegrationService,
    private toaster: Toaster,
    private info: InfoDialogService
  ) {}
}
